import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Tooltip,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Fade,
} from '@mui/material';
import {
  History as HistoryIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Send as SendIcon,
  ChatBubbleOutline as ChatIcon,
  SmartToy as BotIcon,
  Person as UserIcon,
} from '@mui/icons-material';

// UUIDv4 generator
const generateUUID = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (e) { }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Suggestions at the top of the chat
const SUGGESTIONS = [
  "Show lead status breakdown",
  "Which lead has the highest timeline count?",
  "Who has a site visit scheduled today?",
];

// Parser to detect tables in messages and render them as actual tables
const MessageBubble = ({ role, content }) => {
  const isBot = role === 'model' || role === 'bot' || role === 'assistant';

  // Parse lines to detect table format (e.g. pipe or tab separated values)
  const renderContent = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    let inTable = false;
    let tableLines = [];
    const elements = [];
    let textBuffer = [];

    const flushTextBuffer = (key) => {
      if (textBuffer.length > 0) {
        elements.push(
          <Typography
            key={`txt-${key}`}
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              color: isBot ? '#e2e8f0' : '#ffffff',
              fontFamily: '"Outfit", "Inter", sans-serif',
            }}
          >
            {textBuffer.join('\n')}
          </Typography>
        );
        textBuffer = [];
      }
    };

    const flushTable = (key) => {
      if (tableLines.length > 0) {
        // Try parsing the tableLines
        const hasPipe = tableLines.some(l => l.includes('|'));
        let headers = [];
        let rows = [];

        if (hasPipe) {
          const parsed = tableLines
            .map(line => {
              let clean = line.trim();
              if (clean.startsWith('|')) clean = clean.substring(1);
              if (clean.endsWith('|')) clean = clean.slice(0, -1);
              return clean.split('|').map(cell => cell.trim());
            })
            .filter(row => row.length > 0 && !row.every(cell => cell.match(/^-+$/)));

          if (parsed.length > 0) {
            headers = parsed[0];
            rows = parsed.slice(1).filter(r => r.length > 0 && r[0] !== '');
          }
        } else {
          // Tab or space separated fallback
          const parsed = tableLines
            .map(line => line.trim().split(/\t+/))
            .filter(row => row.length > 1);
          if (parsed.length > 0) {
            headers = parsed[0];
            rows = parsed.slice(1);
          }
        }

        if (headers.length > 0) {
          elements.push(
            <TableContainer
              key={`tbl-${key}`}
              component={Paper}
              sx={{
                my: 1.5,
                background: 'rgba(30, 41, 59, 0.5)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                borderRadius: '8px',
                overflowX: 'auto',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              }}
            >
              <Table size="small">
                <TableHead sx={{ background: 'rgba(99, 102, 241, 0.15)' }}>
                  <TableRow>
                    {headers.map((h, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          color: '#818cf8',
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                          borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
                          py: 1,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, ri) => (
                    <TableRow key={ri} sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.03)' } }}>
                      {row.map((cell, ci) => (
                        <TableCell
                          key={ci}
                          sx={{
                            color: '#e2e8f0',
                            fontSize: '0.75rem',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                            py: 0.75,
                          }}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        } else {
          // If parsing failed, just put the raw text back
          textBuffer.push(...tableLines);
        }
        tableLines = [];
      }
    };

    lines.forEach((line, idx) => {
      const isTableSeparator = line.trim().match(/^\|?[\s-]*:?---+:?[\s-|]*$/);
      const isTableLine = line.includes('|') || line.includes('\t') || isTableSeparator;

      if (isTableLine) {
        if (!inTable) {
          flushTextBuffer(idx);
          inTable = true;
        }
        tableLines.push(line);
      } else {
        if (inTable) {
          flushTable(idx);
          inTable = false;
        }
        textBuffer.push(line);
      }
    });

    if (inTable) {
      flushTable(lines.length);
    } else {
      flushTextBuffer(lines.length);
    }

    return elements;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isBot ? 'flex-start' : 'flex-end',
        alignItems: 'flex-start',
        gap: 1.5,
        mb: 2.5,
        width: '100%',
      }}
    >
      {isBot && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 10px rgba(99, 102, 241, 0.4)',
            flexShrink: 0,
          }}
        >
          <BotIcon sx={{ fontSize: 18, color: '#fff' }} />
        </Box>
      )}

      <Box
        sx={{
          maxWidth: '82%',
          background: isBot
            ? 'rgba(15, 23, 42, 0.65)'
            : 'linear-gradient(135deg, rgba(79, 70, 229, 0.8) 0%, rgba(124, 58, 237, 0.8) 100%)',
          backdropFilter: 'blur(12px)',
          border: isBot
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: isBot ? '0px 16px 16px 16px' : '16px 0px 16px 16px',
          boxShadow: isBot
            ? '0 4px 20px rgba(0, 0, 0, 0.15)'
            : '0 4px 20px rgba(99, 102, 241, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.2)',
          p: 1.75,
          color: '#ffffff',
          position: 'relative',
        }}
      >
        {renderContent(content)}
      </Box>

      {!isBot && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)',
            flexShrink: 0,
          }}
        >
          <UserIcon sx={{ fontSize: 18, color: '#fff' }} />
        </Box>
      )}
    </Box>
  );
};

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);

  // Loaders
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);

  // Initialize Session ID
  useEffect(() => {
    let activeSessionId = localStorage.getItem('chatbot_session_id');
    if (!activeSessionId) {
      activeSessionId = generateUUID();
      localStorage.setItem('chatbot_session_id', activeSessionId);
    }
    setSessionId(activeSessionId);
  }, []);

  // Scroll to bottom when messages or loading state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Fetch session history whenever sessionId is ready/updated
  useEffect(() => {
    if (!sessionId) return;
    fetchChatHistory(sessionId);
  }, [sessionId]);

  // Load chat history from backend
  const fetchChatHistory = async (id) => {
    setIsLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/public-chatbot/history/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.status) {
        const historyData = response.data.data;
        let list = [];
        if (Array.isArray(historyData)) {
          list = historyData;
        } else if (historyData && Array.isArray(historyData.messages)) {
          list = historyData.messages;
        } else if (historyData && Array.isArray(historyData.history)) {
          list = historyData.history;
        }

        const parsed = list.map((msg) => ({
          role: msg.role === 'user' ? 'user' : 'model',
          content: msg.content || msg.text || msg.message || '',
        })).filter(msg => msg.content);

        setMessages(parsed);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
      setMessages([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Fetch sessions list from backend
  const fetchSessions = async () => {
    setIsLoadingSessions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/public-chatbot/sessions', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data && response.data.status) {
        setSessions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Toggle sessions list
  const handleToggleHistory = () => {
    const nextState = !showHistory;
    setShowHistory(nextState);
    if (nextState) {
      fetchSessions();
    }
  };

  // Reset or Start New Session
  const handleNewChat = () => {
    const newId = generateUUID();
    localStorage.setItem('chatbot_session_id', newId);
    setSessionId(newId);
    setMessages([]);
    if (showHistory) {
      fetchSessions();
    }
  };

  // Send message
  const handleSend = async (textToSend) => {
    const text = (textToSend || input).trim();
    if (!text || isLoading) return;

    if (!textToSend) {
      setInput('');
    }

    // Append user message directly to UI
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/public-chatbot/ask',
        {
          message: text,
          sessionId: sessionId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.status && response.data.data) {
        const reply = response.data.data.reply || '';
        setMessages((prev) => [...prev, { role: 'model', content: reply }]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: 'model', content: 'Received invalid response format from assistant.' },
        ]);
      }

      // Refresh sessions sidebar if it is currently open
      if (showHistory) {
        fetchSessions();
      }
    } catch (error) {
      console.error('Error asking chatbot:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'model', content: 'Connection error. Please try again later.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 99999, fontFamily: '"Outfit", "Inter", sans-serif' }}>
      {/* Floating Toggle Button */}
      {!isOpen && (
        <IconButton
          onClick={() => setIsOpen(true)}
          sx={{
            width: 56,
            height: 56,
            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            boxShadow: '0 8px 32px 0 rgba(79, 70, 229, 0.4), 0 0 15px rgba(124, 58, 237, 0.3)',
            color: '#ffffff',
            borderRadius: '50%',
            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            '&:hover': {
              transform: 'scale(1.1) rotate(5deg)',
              background: 'linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)',
              boxShadow: '0 12px 40px 0 rgba(79, 70, 229, 0.5), 0 0 20px rgba(124, 58, 237, 0.5)',
            },
          }}
        >
          <ChatIcon sx={{ fontSize: 26 }} />
        </IconButton>
      )}

      {/* Main Glassmorphic Window */}
      {isOpen && (
        <Fade in={isOpen} timeout={300}>
          <Box
            sx={{
              width: { xs: 'calc(100vw - 48px)', sm: showHistory ? '760px' : '420px' },
              height: '620px',
              display: 'flex',
              borderRadius: '24px',
              overflow: 'hidden',
              background: 'rgba(10, 15, 30, 0.88)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(99, 102, 241, 0.25)',
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.6), 0 0 25px rgba(99, 102, 241, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.1)',
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {/* Session Sidebar History list */}
            {showHistory && (
              <Box
                sx={{
                  width: '320px',
                  borderRight: '1px solid rgba(255, 255, 255, 0.08)',
                  background: 'rgba(6, 9, 20, 0.75)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon sx={{ color: '#818cf8', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                    Chat History
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {isLoadingSessions ? (
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={24} sx={{ color: '#818cf8' }} />
                  </Box>
                ) : (
                  <List sx={{ flexGrow: 1, overflowY: 'auto', p: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
                    {sessions.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          No past sessions found.
                        </Typography>
                      </Box>
                    ) : (
                      sessions.map((sess) => {
                        const isCurrent = sess.sessionId === sessionId;
                        return (
                          <ListItem key={sess.sessionId} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                              selected={isCurrent}
                              onClick={() => {
                                setSessionId(sess.sessionId);
                                localStorage.setItem('chatbot_session_id', sess.sessionId);
                              }}
                              sx={{
                                borderRadius: '12px',
                                border: isCurrent ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid transparent',
                                background: isCurrent ? 'rgba(99, 102, 241, 0.12) !important' : 'transparent',
                                '&:hover': {
                                  background: 'rgba(255, 255, 255, 0.04)',
                                },
                                p: 1.5,
                              }}
                            >
                              <ListItemText
                                primary={sess.lastMessage || 'Empty chat session'}
                                secondary={`ID: ${sess.sessionId.substring(0, 8)}...`}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  noWrap: true,
                                  sx: { color: isCurrent ? '#a5b4fc' : '#e2e8f0', fontWeight: isCurrent ? 'bold' : 'normal' },
                                }}
                                secondaryTypographyProps={{
                                  variant: 'caption',
                                  sx: { color: '#64748b', mt: 0.5 },
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                )}
              </Box>
            )}

            {/* Chat Area */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'rgba(15, 23, 42, 0.4)',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#10b981',
                      boxShadow: '0 0 10px #10b981',
                    }}
                  />
                  <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 600, letterSpacing: '0.5px' }}>
                    YES CRM CRM Assistant
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Toggle History">
                    <IconButton size="small" onClick={handleToggleHistory} sx={{ color: showHistory ? '#818cf8' : '#94a3b8' }}>
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="New Chat">
                    <IconButton size="small" onClick={handleNewChat} sx={{ color: '#94a3b8', '&:hover': { color: '#818cf8' } }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Minimize">
                    <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: '#94a3b8', '&:hover': { color: '#f43f5e' } }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Chat Feed */}
              <Box
                ref={containerRef}
                sx={{
                  flexGrow: 1,
                  p: 2.5,
                  overflowY: 'auto',
                  background: 'rgba(6, 9, 20, 0.4)',
                  display: 'flex',
                  flexDirection: 'column',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' },
                }}
              >
                {isLoadingHistory && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} sx={{ color: '#818cf8' }} />
                  </Box>
                )}

                {/* Suggestions / Chips at start of chat */}
                {!isLoadingHistory && messages.length === 0 && (
                  <Box sx={{ my: 'auto', textAlign: 'center', px: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '16px',
                        background: 'rgba(99, 102, 241, 0.1)',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        color: '#818cf8',
                        boxShadow: '0 0 15px rgba(99, 102, 241, 0.15)',
                      }}
                    >
                      <BotIcon sx={{ fontSize: 26 }} />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 'bold', mb: 0.5 }}>
                      Welcome to Yes CRM Assistant
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 3.5 }}>
                      Ask me anything about leads, status, site visits, or metrics.
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: '340px', mx: 'auto' }}>
                      {SUGGESTIONS.map((suggestion, idx) => (
                        <Paper
                          key={idx}
                          onClick={() => handleSend(suggestion)}
                          sx={{
                            p: 1.75,
                            textAlign: 'left',
                            borderRadius: '12px',
                            background: 'rgba(15, 23, 42, 0.5)',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              background: 'rgba(99, 102, 241, 0.08)',
                              borderColor: 'rgba(99, 102, 241, 0.3)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          <Typography variant="caption" sx={{ color: '#a5b4fc', fontWeight: 500 }}>
                            {suggestion}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Messages mapping */}
                {messages.map((msg, index) => (
                  <MessageBubble key={index} role={msg.role} content={msg.content} />
                ))}

                {/* Loading state indicator */}
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <BotIcon sx={{ fontSize: 18, color: '#fff' }} />
                    </Box>
                    <Box
                      sx={{
                        background: 'rgba(15, 23, 42, 0.65)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '0px 16px 16px 16px',
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        gap: 0.5,
                      }}
                    >
                      <span className="typing-dot" style={{ animationDelay: '0ms' }}></span>
                      <span className="typing-dot" style={{ animationDelay: '150ms' }}></span>
                      <span className="typing-dot" style={{ animationDelay: '300ms' }}></span>
                    </Box>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area */}
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                sx={{
                  p: 2,
                  background: 'rgba(15, 23, 42, 0.6)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                }}
              >
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask a question..."
                  variant="outlined"
                  size="small"
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#ffffff',
                      background: 'rgba(6, 9, 20, 0.6)',
                      borderRadius: '16px',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(99, 102, 241, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#818cf8',
                        boxShadow: '0 0 10px rgba(99, 102, 241, 0.3)',
                      },
                      fontSize: '0.85rem',
                      fontFamily: '"Outfit", "Inter", sans-serif',
                    },
                  }}
                />
                <IconButton
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  sx={{
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    color: '#ffffff',
                    borderRadius: '14px',
                    width: 40,
                    height: 40,
                    boxShadow: '0 4px 14px rgba(79, 70, 229, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)',
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'rgba(255, 255, 255, 0.2)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Styled keyframe animations loaded globally via a style block */}
      <style>{`
        @keyframes typing-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .typing-dot {
          width: 6px;
          height: 6px;
          background: #818cf8;
          border-radius: 50%;
          display: inline-block;
          animation: typing-bounce 1s infinite ease-in-out;
        }
      `}</style>
    </Box>
  );
}
